import * as wjcCore from '@mescius/wijmo';

/**
 * Application specific formatting as global set of formatting functions
 * that can be imported by the application components.
 */
export class AppFormat {

    static encodeHtml(text: string | undefined): string {
        if (!text) return '';
        else {
            const helperEl = document.createElement('span');
            helperEl.innerText = text;
            return helperEl.innerHTML;
        }
    }

    static formatFileSize(bytes: number | null | undefined): string {
        if (bytes == null) return "";
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    static formatDate(date: Date | undefined): string {
        if (!date) return "";
        else return wjcCore.format('{date:yyyy-MM-dd}', { date });;
    }

    static formatDateTime(dateTime: Date | null | undefined): string {
        if (!dateTime) return "";
        else return wjcCore.format('{dateTime:yyyy-MM-dd "at" HH:mm}', { dateTime });
    }

    static formatAnyAsDateTime(date: any): string {
        if (typeof date === 'string') {
            try { return AppFormat.formatDateTime(new Date(date)); }
            catch (e) { return date }
        } else {
            return AppFormat.formatDateTime(date);
        }
    }

    static formatDuration(start: Date | undefined, end: Date | undefined): string {
        if (!start || !end) return "";
        const sec_num = (end.getTime() - start.getTime())/1000;
        const hours   = Math.floor(sec_num / 3600);
        const minutes = Math.floor((sec_num - (hours * 3600)) / 60);
        const seconds = sec_num - (hours * 3600) - (minutes * 60);

        let result = '';
        if (hours > 0) result += `${hours}h `;
        if (minutes > 0) result += `${minutes}m `;
        if (seconds > 0) result += `${seconds.toFixed(1)}s `;
        return result;
    }

    static formatInteger(value: number | undefined): string {
        if (value == undefined) return "";
        else return wjcCore.format(`{value:n}`, { value });
    }

    static formatPercent(value: number | undefined, precision: number = 0): string {
        if (value == undefined) return "";
        else return wjcCore.format(`{value:p${precision}}`, { value });
    }

    static formatTagHtml(tag: string | undefined): string {
        if (!tag) return '';
        else {
            const tagTextEl = document.createElement('span');
            tagTextEl.classList.add('mdl-chip__text');
            tagTextEl.innerText = tag;

            const tagEl = document.createElement('span');
            tagEl.classList.add('mdl-chip', 'small-chip');
            tagEl.appendChild(tagTextEl);

            return tagEl.outerHTML;
        }
    }

    static formatTagsHtml(tags: string[] | undefined): string {
        return (tags ?? []).map(tag => AppFormat.formatTagHtml(tag)).join('');
    }

    static toEndOfDay(date: Date | undefined): Date | undefined {
        if (date == null) return undefined;
        else {
            const result = new Date(date);
            result.setDate(result.getDate() + 1);
            return result;
        }
    }

    /**
     * Creates an ISO date string that is specific to the browser's time zone.
     * This is in contrast to the Date.toIsoString() method, which returns
     * the ISO date string in UTC. Pure dates, sent as ISO string from Date objects
     * will be parsed be Spring boot, but cut off at 00:00 in the UTC time zone,
     * which systematically shifts the date by -1 to the preceding day (in the
     * middle European timezone).
     *
     * Code taken frm https://stackoverflow.com/questions/17415579/how-to-iso-8601-format-a-date-with-timezone-offset-in-javascript
     *
     * @param date
     */
    static toIsoLocalDateTimeString(date: Date | undefined): string {
        if (date == null) return '';

        const tzo = -date.getTimezoneOffset();
        const dif = tzo >= 0 ? '+' : '-';
        const pad = (num: number) =>  (num < 10 ? '0' : '') + num;

        return date.getFullYear() +
            '-' + pad(date.getMonth() + 1) +
            '-' + pad(date.getDate()) +
            'T' + pad(date.getHours()) +
            ':' + pad(date.getMinutes()) +
            ':' + pad(date.getSeconds() + date.getMilliseconds() / 1000);
    }
}
